// src/components/PaywallDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Print as PrintIcon,
  Download as DownloadIcon,
  AllInclusive as UnlimitedIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  CreditCard as CreditCardIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useStripe } from '../contexts/FirebaseStripeContext';
import { useWaitlist } from '../hooks/useWaitlist';

interface PaywallDialogProps {
  open: boolean;
  onClose: () => void;
}

const PaywallDialog: React.FC<PaywallDialogProps> = ({
  open,
  onClose
}) => {
  const { subscribe, products } = useStripe();
  const [isLoading, setIsLoading] = useState(false);
  const { hasSubscriptionAccess, config } = useWaitlist();

  const handleSubscribe = async () => {
    if (!hasSubscriptionAccess) {
      return;
    }

    const monthlyProduct = products.find(p => p.name.includes('1$ Monthly'));
    const monthlyPrice = monthlyProduct?.prices.find(p => p.interval === 'month');
    
    if (!monthlyPrice) {
      console.error('No monthly price found');
      return;
    }

    setIsLoading(true);
    try {
      await subscribe(monthlyPrice.id);
    } catch (error) {
      console.error('Error subscribing:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // If user doesn't have subscription access, show waitlist message
  if (!hasSubscriptionAccess) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          pt: 3
        }}>
          <AccessTimeIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
            Coming Soon!
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {config.MESSAGES.WAITLIST_ENROLLMENT.title}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {config.MESSAGES.WAITLIST_ENROLLMENT.subtitle}
            </Typography>
            <Typography variant="body2">
              {config.MESSAGES.WAITLIST_ENROLLMENT.description}
            </Typography>
          </Alert>

          <Box sx={{ 
            bgcolor: 'background.default', 
            p: 3, 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
              What's Coming in {config.LAUNCH_DATE}:
            </Typography>
            
            <List>
              {config.UPCOMING_FEATURES.map((feature, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    {index === 0 && <PrintIcon color="primary" />}
                    {index === 1 && <DownloadIcon color="primary" />}
                    {index === 2 && <CheckIcon color="primary" />}
                    {index === 3 && <CreditCardIcon color="primary" />}
                  </ListItemIcon>
                  <ListItemText 
                    primary={feature}
                    secondary={index === 0 ? "Print as many lease agreements as you need" :
                              index === 1 ? "Download and save lease agreements" :
                              index === 2 ? "California-focused lease templates (self-help, not legal advice)" :
                              "Just $1/month for unlimited access"}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        
        <Divider />
        
        <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
          <Button 
            onClick={onClose}
            variant="contained"
            sx={{ 
              minWidth: 120,
              fontWeight: 600
            }}
          >
            Got It!
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 1,
        pt: 3
      }}>
        <PrintIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
          Unlock Unlimited Printing
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
            $1
            <Typography component="span" variant="h6" sx={{ fontWeight: 400 }}>
              /month
            </Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cancel anytime • No hidden fees
          </Typography>
        </Box>

        <List sx={{ mb: 3 }}>
          <ListItem>
            <ListItemIcon>
              <UnlimitedIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Unlimited Lease Printing"
              secondary="Print as many lease agreements as you need"
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <DownloadIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="PDF Downloads"
              secondary="Download and save lease agreements"
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <CheckIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Professional Format"
              secondary="California-focused lease templates (self-help)"
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <CancelIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Cancel Anytime"
              secondary="No long-term commitment required"
            />
          </ListItem>
        </List>

        <Box sx={{ 
          bgcolor: 'background.default', 
          p: 2, 
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            <strong>🔒 Secure Payment</strong><br />
            Powered by Stripe • Your payment info is never stored
          </Typography>
        </Box>
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
        <Button 
          onClick={onClose}
          disabled={isLoading}
          sx={{ minWidth: 120 }}
        >
          Not Now
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubscribe}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : <CreditCardIcon />}
          sx={{ 
            minWidth: 160,
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          {isLoading ? 'Processing...' : 'Subscribe Now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaywallDialog; 
